import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../../../api/api";
import { getPracticeId } from "../../../../config/practice";


const Phq9Link = () => {
  const { patientid, filename } = useParams();

  const [loading, setLoading] = useState(true);
  const [json, setJson] = useState(null);

  const practiseid = getPracticeId();
  const SCREENSHOT_TARGET_ID = "screenshot-ready-container";

  useEffect(() => {
    const fetchHtml = async () => {
      try {
        const response = await api.get(
          `/v1/${practiseid}/pcard/${patientid}/formHistory/${filename}`
        );

        if (response.status === 200) {
          setJson(response.data?.json_data || null);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setTimeout(() => setLoading(false), 300);
      }
    };

    fetchHtml();
  }, [filename]);

  if (loading)
    return (
      <div className="flex items-center justify-center h-screen text-xl font-semibold">
        Loading...
      </div>
    );

  if (!json)
    return (
      <div className="flex items-center justify-center h-screen text-xl text-red-600">
        No Data Found
      </div>
    );

  // ---- PHQ9 Questions ----
  const questions = [
    { q: "Little interest or pleasure in doing things", key: "qanswer_4_1" },
    { q: "Feeling down, depressed or hopeless", key: "qanswer_4_2" },
    { q: "Trouble falling or staying asleep, or sleeping too much", key: "qanswer_4_3" },
    { q: "Feeling tired or having little energy", key: "qanswer_4_4" },
    { q: "Poor appetite or overeating", key: "qanswer_4_5" },
    {
      q: "Feeling bad about yourself, or feeling like a failure",
      key: "qanswer_4_6",
    },
    { q: "Trouble concentrating", key: "qanswer_4_7" },
    {
      q: "Moving or speaking slowly OR feeling restless",
      key: "qanswer_4_8",
    },
    { q: "Thoughts of self harm or being better off dead", key: "qanswer_4_9" },
  ];

  const optionLabels = ["Not at all", "Several days", "More than half the days", "Nearly every day"];

  // -------------------------------------------------------------------------

  return (
    <>
  

      {/* Page Background */}
      <div  id={json.patient_name?SCREENSHOT_TARGET_ID:undefined} className="min-h-screen bg-[#f7f7f7] flex justify-center py-10 px-3">

        {/* Center Card */}
        <div className="bg-white text-start shadow-lg rounded-lg p-6 max-w-4xl w-full">

          <h2 className="text-2xl font-bold mb-4">{json.form_name}</h2>

          {/* Intro Section */}
          <div className="mb-4">
            <p className="font-bold text-lg mb-1">Are you feeling depressed?</p>
            <p className="text-gray-700">
              This questionnaire helps evaluate depressive symptoms based on your feelings over the last 2 weeks.
              Share these responses with your healthcare provider. This is not a diagnostic tool.
            </p>
          </div>

          <p className="mb-4 text-gray-700 font-semibold">
            Over the last 2 weeks, how often have you been bothered by the following problems?
          </p>

          {/* PHQ9 Table */}
          <table className="w-full border-collapse mb-6">
            <thead>
              <tr>
                <th className="text-left p-2"></th>
                {optionLabels.map((label, idx) => (
                  <th key={idx} className="text-center p-2 font-semibold">{label}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {questions.map((q, idx) => (
                <tr key={idx}>
                  <td className="p-2 font-bold">{q.q}</td>
                  {[0, 1, 2, 3].map((val) => (
                    <td key={val} className="text-center p-2">
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-sans border ${
                          Number(json[q.key]) === val
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white border-2 border-gray-300 text-black"
                        }`}
                      >
                        {val}
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Score Summary */}
          <div className="mt-4">
            <h3 className="font-bold text-lg mb-2">Score Summary</h3>
            <p>Not at all: {json.several_count}</p>
            <p>Several days: {json.halfday_count}</p>
            <p>More than half the days: {json.halfday_count}</p>
            <p>Nearly every day: {json.everyday_count}</p>

            <p className="font-bold mt-2 text-lg">Total Score: {json.total_score}</p>
          </div>

          {/* Difficulty */}
          <div className="mt-6">
            <h3 className="font-bold text-lg mb-2">How difficult have these problems made things for you?</h3>

            {["Not difficult at all", "Somewhat difficult", "Very difficult", "Extremely difficult"].map((opt) => (
              <span
                key={opt}
                className={`px-3 py-1 rounded-full text-sm font-sans mr-2 mb-2 inline-block ${
                  json.qanswer_4_10 === opt
                    ? "bg-blue-600 text-white border border-blue-600"
                    : "bg-white border-2 border-gray-300 text-black"
                }`}
              >
                {opt}
              </span>
            ))}
          </div>

          {/* Footer Note */}
          <div className="mt-6 p-4 bg-gray-100 border-l-4 border-blue-600 rounded-md text-gray-800">
            <p className="font-semibold mb-2">For healthcare professionals:</p>
            <p>
              All questionnaire responses should be clinically reviewed. A diagnosis must consider other symptoms,
              patient understanding, and exclusion of substance effects.
            </p>
          </div>

        </div>
      </div>
    </>
  );
};

export default Phq9Link;
